import { useMemo, useState } from 'react';
import { useCreateCryptoSale, useMemberOptions } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Field, Modal, MoneyInput } from '@/components/ui';
import { LeadPicker } from '@/components/leads/LeadPicker';
import { IconCheck } from '@/components/Icons';
import { formatDate, todayISO } from '@/lib/format';
import {
  addDaysISO, CRYPTO_CURRENCIES, formatCrypto, installmentsForLimit, MAX_INSTALLMENTS, splitEvenly,
} from '@/lib/crypto';
import type { CryptoCurrency } from '@/lib/types';

interface NewCryptoSaleModalProps {
  onClose: () => void;
}

const INTERVALS = [
  { value: 1, label: 'Uma por dia' },
  { value: 2, label: 'A cada 2 dias' },
  { value: 7, label: 'Uma por semana' },
];

/**
 * Nova venda em cripto: o total, em quantas parcelas e a partir de quando. O
 * limite de saque da corretora sugere o número de parcelas; a prévia mostra o
 * carnê antes de criar, e os valores podem ser ajustados à mão.
 */
export function NewCryptoSaleModal({ onClose }: NewCryptoSaleModalProps) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const members = useMemberOptions();
  const create = useCreateCryptoSale();

  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState<CryptoCurrency>('USDT');
  const [total, setTotal] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(0);
  const [count, setCount] = useState(4);
  const [firstDueDate, setFirstDueDate] = useState(todayISO());
  const [intervalDays, setIntervalDays] = useState(1);
  const [customAmounts, setCustomAmounts] = useState<number[] | null>(null);
  const [memberId, setMemberId] = useState('');
  const [lead, setLead] = useState<{ id: string; name: string } | null>(null);
  const [exchange, setExchange] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const evenAmounts = useMemo(() => splitEvenly(total, count), [total, count]);
  const amounts = customAmounts ?? evenAmounts;
  const customSum = amounts.reduce((sum, amount) => sum + amount, 0);

  // Mudar o total ou o número de parcelas volta à divisão igual.
  function changeTotal(cents: number) {
    setTotal(cents);
    setCustomAmounts(null);
    if (dailyLimit > 0) setCount(installmentsForLimit(cents, dailyLimit));
  }
  function changeCount(value: number) {
    setCount(Math.max(1, Math.min(MAX_INSTALLMENTS, Math.round(value) || 1)));
    setCustomAmounts(null);
  }
  function changeLimit(cents: number) {
    setDailyLimit(cents);
    if (cents > 0 && total > 0) changeCount(installmentsForLimit(total, cents));
  }
  function changeAmount(index: number, cents: number) {
    setCustomAmounts(amounts.map((amount, position) => (position === index ? cents : amount)));
  }

  function problem(): string | null {
    if (description.trim().length < 2) return 'Informe o que foi vendido.';
    if (total <= 0) return 'Informe o valor total.';
    if (!firstDueDate) return 'Informe o dia da primeira parcela.';
    if (amounts.some((amount) => amount <= 0)) return 'Toda parcela precisa ter valor — use menos parcelas.';
    if (customSum !== total) return `As parcelas somam ${formatCrypto(customSum, currency)}; o total é ${formatCrypto(total, currency)}.`;
    if (isAdmin && !memberId) return 'Escolha o membro que fez a venda.';
    return null;
  }

  async function submit() {
    const found = problem();
    if (found) return setError(found);
    setError(null);
    try {
      await create.mutateAsync({
        description: description.trim(),
        currency,
        totalAmount: total,
        installments: count,
        firstDueDate,
        intervalDays,
        ...(customAmounts ? { amounts: customAmounts } : {}),
        ...(exchange.trim() ? { exchange: exchange.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(lead ? { leadId: lead.id } : {}),
        ...(memberId ? { memberId } : {}),
      });
      toast.success(`Carnê criado: ${count} ${count === 1 ? 'parcela' : 'parcelas'} de ${description.trim()}.`);
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title="Nova venda em cripto"
      width={700}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={create.isPending}>
            {create.isPending ? <span className="spinner" /> : <IconCheck />} Criar carnê
          </button>
        </>
      }
    >
      <Field label="O que foi vendido *">
        <input
          className="inp"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Ex.: Mastermind"
          maxLength={200}
          autoFocus
        />
      </Field>

      <div className="grid grid3">
        <Field label="Moeda">
          <select className="inp" value={currency} onChange={(event) => setCurrency(event.target.value as CryptoCurrency)}>
            {CRYPTO_CURRENCIES.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        <Field label={`Valor total (${currency}) *`}>
          <MoneyInput value={total} onChange={changeTotal} />
        </Field>
        <Field label="Limite de saque por dia" hint="Opcional: sugere as parcelas.">
          <MoneyInput value={dailyLimit} onChange={changeLimit} />
        </Field>
      </div>

      <div className="grid grid3">
        <Field label="Parcelas *">
          <input
            className="inp num"
            type="number"
            min={1}
            max={MAX_INSTALLMENTS}
            value={count}
            onChange={(event) => changeCount(Number(event.target.value))}
          />
        </Field>
        <Field label="Primeira parcela *">
          <input className="inp" type="date" value={firstDueDate} onChange={(event) => setFirstDueDate(event.target.value)} />
        </Field>
        <Field label="Frequência">
          <select className="inp" value={intervalDays} onChange={(event) => setIntervalDays(Number(event.target.value))}>
            {INTERVALS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </Field>
      </div>

      {total > 0 ? (
        <section aria-label="Prévia do carnê">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span className="field-label" style={{ margin: 0 }}>O carnê</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {customAmounts ? 'valores ajustados à mão' : 'parcelas iguais; a última leva o arredondamento'}
            </span>
            {customAmounts ? (
              <button type="button" className="link-btn" style={{ fontSize: 12 }} onClick={() => setCustomAmounts(null)}>
                Voltar para parcelas iguais
              </button>
            ) : null}
          </div>
          <div className="carne-preview">
            {amounts.map((amount, index) => (
              <div key={index} className="carne-preview-item">
                <span className="muted num">{index + 1}ª · {formatDate(addDaysISO(firstDueDate || todayISO(), index * intervalDays))}</span>
                <MoneyInput value={amount} onChange={(cents) => changeAmount(index, cents)} />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className={isAdmin ? 'grid grid2' : undefined}>
        <Field label="Corretora">
          <input className="inp" value={exchange} onChange={(event) => setExchange(event.target.value)} placeholder="Ex.: Binance" maxLength={60} />
        </Field>
        {isAdmin ? (
          <Field label="Membro que vendeu *" hint="Recebe cada parcela paga no ranking.">
            <select className="inp" value={memberId} onChange={(event) => setMemberId(event.target.value)}>
              <option value="">Selecione o membro…</option>
              {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
            </select>
          </Field>
        ) : null}
      </div>

      <LeadPicker value={lead?.id ?? ''} valueName={lead?.name ?? ''} isAdmin={isAdmin} onChange={(picked) => setLead(picked)} />

      <Field label="Observação">
        <input className="inp" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Opcional" maxLength={500} />
      </Field>

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
    </Modal>
  );
}
